<template>
  <div class="content d-flex">
    <ManageSidebar></ManageSidebar>

    <section class="pl-0" :class="enabled ? 'partner-enabled' : 'partner-disabled'">
      <div class="d-flex align-baseline">
        <h1 class="mr-8">Partner profile</h1>
        <v-btn text @click="editPartner()" v-if="enabled">
          <v-icon small class="mr-2">mdi-pencil</v-icon>Edit
        </v-btn>
        <v-btn text @click="goHistory()">
          <svg
              class="mr-2"
              width="13"
              height="14"
              viewBox="0 0 10 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M0.5 0C0.223877 0 0 0.223862 0 0.5V10.5C0 10.7761 0.223877 11 0.5 11H5.05054C4.40222 10.3647 4 9.47934 4 8.5C4 6.567 5.56702 5 7.5 5C8.0368 5 8.54535 5.12085 9 5.33682V0.5C9 0.223862 8.77612 0 8.5 0H0.5ZM1 2H7V3H1V2ZM4 4H1V5H4V4Z"
                fill="#191919"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M7.5 11C8.88074 11 10 9.88071 10 8.5C10 7.11929 8.88074 6 7.5 6C6.11926 6 5 7.11929 5 8.5C5 9.88071 6.11926 11 7.5 11ZM7 7H7.66669V8.33333H9V9H7.66669H7V8.33333V7Z"
                fill="#191919"
              />
            </svg>History
        </v-btn>
      </div>

      <div class="partner" v-if="partner">
        <div class="mb-8">
          <h2>Basic information</h2>

          <div class="basic-field d-flex align-baseline mb-4">
            <p>Partner</p>
            <p>{{ partner.name }}</p>
          </div>

          <div class="basic-field d-flex align-top mb-6">
            <p>Logo</p>
            <img :src="partner.logo" />
          </div>

          <div class="basic-field d-flex align-baseline mb-4">
            <p>Access</p>
            <p>
              <v-chip
                v-for="access in partner.access"
                :key="access"
                class="mr-3"
                :color="enabled ? 'black' : 'grey'"
                text-color="white"
                >{{ displayAccess(access) }}</v-chip
              >
            </p>
          </div>

          <div class="basic-field d-flex align-baseline mb-4">
            <p>Status</p>
            <p v-if="partner.status">
              <v-chip
                class="text-uppercase"
                :color="enabled ? 'black' : 'grey'"
                text-color="white">{{
                partner.status
              }}</v-chip>
              <span class="ml-2">{{
                constants.DeveloperStatuses.getDescription(partner.status)
              }}</span>
            </p>
            <p v-else>
              None &mdash;
              <a href="" @click.prevent="editPartner()">edit the partner</a> to
              set it's status.
            </p>
          </div>

          <div class="basic-field mb-4">
            <p class="wide">Company description</p>
            <p class="value">{{ partner.description }}</p>
          </div>
        </div>

        <div class="mb-8">
          <h2>Technical contact</h2>

          <div
            v-if="partner.contacts"
            class="contact-field d-flex align-baseline mb-4"
          >
            <p>Name</p>
            <v-text-field
              required
              outlined
              height="46"
              dense
              readonly
              class="text-field-outlined-overrides"
              append-icon="mdi-content-copy"
              @click:append="clickCopy(partner.contacts.technical.name)"
              v-model="partner.contacts.technical.name"
            ></v-text-field>
          </div>

          <div
            v-if="partner.contacts"
            class="contact-field d-flex align-baseline mb-4"
          >
            <p>Email</p>
            <v-text-field
              required
              outlined
              height="46"
              dense
              readonly
              class="text-field-outlined-overrides"
              append-icon="mdi-content-copy"
              @click:append="clickCopy(partner.contacts.technical.email)"
              v-model="partner.contacts.technical.email"
            ></v-text-field>
          </div>

          <p v-if="!partner.contacts">None</p>
        </div>

        <div class="mb-8">
          <h2>URLs</h2>

          <div
            v-if="partner.urls"
            class="contact-field d-flex align-baseline mb-4"
          >
            <p>Zendesk</p>
            <v-text-field
              required
              outlined
              height="46"
              dense
              readonly
              class="text-field-outlined-overrides"
              append-icon="mdi-content-copy"
              @click:append="clickCopy(partner.urls.zendesk)"
              v-model="partner.urls.zendesk"
            ></v-text-field>
          </div>

          <div
            v-if="partner.urls"
            class="contact-field d-flex align-baseline mb-4"
          >
            <p>Confluence</p>
            <v-text-field
              required
              outlined
              height="46"
              dense
              readonly
              class="text-field-outlined-overrides"
              append-icon="mdi-content-copy"
              @click:append="clickCopy(partner.urls.confluence)"
              v-model="partner.urls.confluence"
            ></v-text-field>
          </div>
        </div>

        <div>
          <h2>Credentials</h2>

          <div v-if="partner.credentials && enabled" class="d-flex flex-wrap">
            <v-card
              v-for="credential in partner.credentials"
              :key="credential.name.value"
              class="credential"
              :class="isActiveCredential(credential) ? 'full' : ''"
            >
              <v-card-title
                v-if="!isActiveCredential(credential)"
                class="px-8 py-6"
              >
                {{ credential.name.value }}
              </v-card-title>

              <v-card-text
                class="px-8 pt-8 text-center grey--text text--darken-4"
                v-if="!isActiveCredential(credential)"
              >
                <p class="mb-4">
                  <v-icon class="grey--text text--darken-4"
                    >mdi-open-in-new</v-icon
                  >
                </p>
                <p>
                  Open this card to view a blurred version of your credentials.
                </p>
              </v-card-text>

              <v-card-text
                class="pa-8 grey--text text--darken-4"
                v-if="isActiveCredential(credential)"
              >
                <Credential v-bind:initialCredential="credential"></Credential>
              </v-card-text>

              <v-card-actions
                class="pa-8 justify-center"
                v-if="!isActiveCredential(credential)"
              >
                <v-btn
                  width="187"
                  height="48"
                  outlined
                  @click="setActiveCredential(credential)"
                  >Open</v-btn
                >
              </v-card-actions>
            </v-card>
          </div>

          <p v-if="!partner.credentials?.length" class="mb-8">None</p>
        </div>

        <div class="mb-8">
          <div class="d-flex">
            <h2>Users</h2>

            <div class="flex-grow-1 text-right">
              <v-btn outlined @click="addUser()" width="187" height="48" :disabled="!enabled">
                <v-icon small class="mr-3">mdi-account-plus</v-icon>
                Add user
              </v-btn>
            </div>
          </div>

          <Users
            ref="users"
            v-bind:users="enabled ? partner.users : []"
            v-bind:headers="usersHeaders"
            v-bind:partnerID="$route.params.partnerID"
          ></Users>
        </div>

        <div class="mb-8">
          <h2 id="bills">Billing</h2>

          <div v-if="partner.billing" class="d-flex flex-gap-2">
            <v-card outlined class="flex-1">
              <v-card-title class="d-flex justify-space-between">
                <span>{{
                  partner.billing.enabled ? 'Enabled' : 'Disabled'
                }}</span>
                <v-icon
                  size="x-large"
                  :color="partner.billing.enabled ? 'success' : 'error'"
                  >{{
                    partner.billing.enabled
                      ? 'mdi-check-circle'
                      : 'mdi-alert-circle'
                  }}</v-icon
                >
              </v-card-title>
              <v-card-text>
                <span v-if="partner.billing.enabled"
                  >Billing is enabled for this partner. The partner billing
                  system will create monthly reports for them.</span
                >
                <span v-else
                  >Billing is disabled for this partner. The partner billing
                  system will not create monthly reports for them.</span
                >
              </v-card-text>
            </v-card>

            <v-card outlined class="flex-1">
              <v-card-title>
                <span class="text-capitalize">{{
                  constants.DeveloperBillingModels.getTitle(
                    partner.billing.model
                  )
                }}</span>
              </v-card-title>
              <v-card-text
                >This partner is charged for the number of active locks per
                month.</v-card-text
              >
            </v-card>

            <v-card v-if="partner.billing.rate" outlined class="flex-1">
              <v-card-title>{{
                new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(partner.billing.rate)
              }}</v-card-title>
              <v-card-text
                >The amount this partner is charged per lock per
                month.</v-card-text
              >
            </v-card>
          </div>

          <v-card
            v-if="partner.billing?.rateTiered"
            outlined
            class="flex-1 mt-6"
          >
            <v-card-title>Tiered</v-card-title>
            <v-card-text>
              <p>
                The amount this partner is charged per lock per month varies
                depending on how many locks they have.
              </p>

              <table class="rate-table">
                <tbody>
                  <tr>
                    <th class="text-left">Locks</th>
                    <td
                      v-for="item in partner.billing.rateTiered"
                      :key="item.locks"
                    >
                      {{ new Intl.NumberFormat('en-US').format(item.min) }} -
                      {{
                        item.max
                          ? new Intl.NumberFormat('en-US').format(item.max)
                          : 'Infinity'
                      }}
                    </td>
                  </tr>
                  <tr>
                    <th class="text-left">Rate</th>
                    <td
                      v-for="item in partner.billing.rateTiered"
                      :key="item.locks"
                    >
                      {{
                        new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(item.rate)
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-card-text>
          </v-card>

          <h3 class="mt-8">Past Bills</h3>
          <BillsList
            v-if="partner.bills && partner.bills.length"
            v-bind:bills="partner.bills"
            v-bind:headers="billHeaders"
          ></BillsList>

          <p v-if="!partner.bills?.length" class="mb-8">None</p>
        </div>
      </div>
    </section>

    <v-dialog v-if="partner" v-model="dialogs.edit" width="680" height="300" persistent>
      <v-card>
        <v-card-title class="pa-8">
          <h1 class="mb-0">Edit partner</h1>
        </v-card-title>
        <v-card-text class="px-8 pb-8 grey--text text--darken-4">
          <v-form
            class="form"
            ref="form"
            @submit.prevent="editPartnerConfirm()"
          >
            <div class="d-flex align-baseline">
              <p class="field-name">Name</p>
              <p class="flex-grow-1 mx-4 mb-0">
                <v-text-field
                  required
                  outlined
                  height="46"
                  dense
                  class="text-field-outlined-overrides"
                  v-model="editingPartner.name"
                  :rules="validation.notEmpty"
                ></v-text-field>
              </p>
            </div>

            <div class="d-flex align-top">
              <p class="field-name">Description</p>
              <p class="flex-grow-1 mx-4 mb-0">
                <v-textarea
                  required
                  outlined
                  dense
                  class="text-field-outlined-overrides"
                  v-model="editingPartner.description"
                  :rules="validation.notEmpty"
                ></v-textarea>
              </p>
            </div>

            <div class="d-flex align-baseline">
              <p class="field-name">Logo</p>
              <p class="flex-grow-1 mx-4 mb-0">
                <v-text-field
                  required
                  outlined
                  height="46"
                  dense
                  class="text-field-outlined-overrides"
                  v-model="editingPartner.logo"
                  :rules="validation.notEmpty"
                ></v-text-field>
              </p>
            </div>

            <div class="d-flex align-baseline">
              <p class="field-name">Access</p>
              <p class="flex-grow-1 mx-4">
                <v-switch
                  v-for="(access, index) in constants.DeveloperAccessTypes"
                  :key="index"
                  v-model="editingPartner.access"
                  :label="displayAccess(access)"
                  :value="access"
                  inset
                  class="d-inline-block access_switch"
                  :rules="access === constants.DeveloperAccessTypes.PII ? [] : validation.access"
                ></v-switch>
               
              </p>
            </div>

            <div class="d-flex align-baseline">
              <p class="field-name">Status</p>
              <p class="mx-4">
                <v-btn-toggle v-model="editingPartner.status" color="primary">
                  <v-btn
                    v-for="status in [constants.DeveloperStatuses.EVALUATING, constants.DeveloperStatuses.BUILDING, constants.DeveloperStatuses.LIVE]"
                    :key="status"
                    :value="status"
                    :disabled="
                      !constants.DeveloperStatuses.validateTransition(
                        initialStatus,
                        status
                      )
                    "
                  >
                    {{ status.toUpperCase() }}
                  </v-btn>
                </v-btn-toggle>
              </p>
            </div>

            <p id="statusDescription">
              {{ constants.DeveloperStatuses.getDescription(editingPartner.status) }}
            </p>

            <h3 class="mt-4">Technical contact</h3>

            <div class="d-flex align-baseline">
              <p class="field-name">Name</p>
              <p class="flex-grow-1 mx-4 mb-0">
                <v-text-field
                  required
                  outlined
                  height="46"
                  dense
                  class="text-field-outlined-overrides"
                  v-model="editingPartner.contacts.technical.name"
                  :rules="validation.notEmpty"
                ></v-text-field>
              </p>
            </div>

            <div class="d-flex align-baseline">
              <p class="field-name">Email</p>
              <p class="flex-grow-1 mx-4 mb-0">
                <v-text-field
                  required
                  outlined
                  height="46"
                  dense
                  class="text-field-outlined-overrides"
                  v-model="editingPartner.contacts.technical.email"
                  :rules="validation.notEmpty"
                ></v-text-field>
              </p>
            </div>

            <h3 class="mt-4">URLs</h3>

            <div class="d-flex align-baseline">
              <p class="field-name">Zendesk</p>
              <p class="flex-grow-1 mx-4 mb-0">
                <v-text-field
                  outlined
                  height="46"
                  dense
                  class="text-field-outlined-overrides"
                  v-model="editingPartner.urls.zendesk"
                  :rules="validation.secureUrlOptional"
                ></v-text-field>
              </p>
            </div>

            <div class="d-flex align-baseline">
              <p class="field-name">Confluence</p>
              <p class="flex-grow-1 mx-4 mb-0">
                <v-text-field
                  outlined
                  height="46"
                  dense
                  class="text-field-outlined-overrides"
                  v-model="editingPartner.urls.confluence"
                  :rules="validation.secureUrlOptional"
                ></v-text-field>
              </p>
            </div>

            <div class="mt-8">
              <v-btn width="187" height="48" class="mr-5" dark type="submit"
              :disabled="progressing"
                >Edit<v-progress-circular indeterminate v-if="progressing"></v-progress-circular></v-btn
              >
              <v-btn
                width="187"
                height="48"
                outlined
                @click="dialogs.edit = false"
                >Cancel</v-btn
              >
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';

import constants from 'august-constants';

import BillsList from '../components/BillsList.vue';
import Credential from '../components/Credential.vue';
import ManageSidebar from '../components/ManageSidebar.vue';
import Users from '../components/Users.vue';

import config from '../config';
import util from '../util';

export default {
  components: {
    BillsList,
    Credential,
    Users,
    ManageSidebar,
  },
  data: () => {
    return {
      constants,
      usersHeaders: config.users.headers,
      billHeaders: config.bills.headers,
      partner: {
        access: [],
        contacts: {
          technical: {},
        },
        urls: {},
      },
      editingPartner: {
        access: [],
        contacts: {
          technical: {},
        },
        urls: {},
      },
      initialStatus: undefined,
      activeCredential: null,
      dialogs: {
        edit: false,
        delete: false,
      },
      validation: config.formValidations,
      progressing: false,
    };
  },
  created: async function () {
    const partnerID = this.$route.params.partnerID;
    await this.getPartner(partnerID);
  },
  methods: {
    clickCopy: util.clickCopy,
    getPartner: async function (partnerID) {
      const options = {
        method: 'GET',
        ...config.defaultFetchOptions,
      };
      const result = await util.fetch(
        `${this.$store.state.apiHost}/partners/${partnerID}`,
        options
      );

      if (result.ok) {
        const partner = await result.json();
        this.partner = partner;
        this.initialStatus = partner.status;
      } else {
        this.$store.commit('snackbar', {
          text: 'Failed to get partner.',
          color: 'error',
        });
      }
    },
    editPartner: function () {
      this.dialogs.edit = true;
      this.editingPartner = _.cloneDeep(this.partner);
    },
    goHistory: function () {
      this.$router.push({
        name: 'OperationHistory',
        params: {
          partnerID: this.$route.params.partnerID,
        },
      });
    },
    editPartnerConfirm: async function () {
      const isValid = this.$refs.form.validate();
      if (!isValid) {
        return;
      }
      if (this.progressing || _.isEmpty(this.editingPartner)) {
        return;
      }

      const data = {
        name: this.editingPartner.name,
        description: this.editingPartner.description,
        logo: this.editingPartner.logo,
        access: this.editingPartner.access,
        status: this.editingPartner.status,
        contacts: this.editingPartner.contacts,
        urls: this.editingPartner.urls,
      };
      const options = {
        withCredentials: true,
      };

      try {
        this.progressing = true;
        await util.refreshAccessTokenIfNeeded();
        await axios.put(
          `${this.$store.state.apiHost}/partners/${this.$route.params.partnerID}`,
          data,
          options
        );
        await this.getPartner(this.$route.params.partnerID);
        this.$store.commit('snackbar', {
          text: 'Partner updated',
          color: 'success',
        });
      } catch (error) {
        this.$store.commit('snackbar', {
          text: 'Failed to update partner.',
          color: 'error',
        });
      } finally {
        this.progressing = false;
        this.dialogs.edit = false;
      }
    },
    addUser: function () {
      this.$refs.users.addUser();
    },
    isActiveCredential: function (credential) {
      if (!this.activeCredential) {
        return false;
      }

      return this.activeCredential.name.value === credential.name.value;
    },
    setActiveCredential: function (credential) {
      this.activeCredential = credential;
    },
    removeActiveCredential: function () {
      this.activeCredential = null;
    },
    displayAccess(access) {
      return util.getAccessDisplay(access);
    },
  },
  computed: {
    enabled: function() {
      return this.partner.status !== constants.DeveloperStatuses.DISABLED;
    }
  }
};
</script>

<style lang="scss">
.delete-partner .v-card__title .v-icon {
  font-size: 80px;
}

.basic-field {
  p.value {
    word-break: break-all;
  }

  p:first-child {
    width: 6rem;
    font-size: 1.125rem;
    font-weight: bold;
  }

  p.wide:first-child {
    width: auto;
  }
}

.contact-field {
  width: 650px;

  p:first-child {
    width: 10rem;
    font-size: 1.125rem;
    font-weight: bold;
    margin-bottom: 0;
  }
}

.credential.v-card {
  width: 300px;
  transition: width 0.25s ease-in-out;
  margin: 0 2rem 2rem 0;
  box-shadow: 0px 7px 20px rgba(0, 0, 0, 0.1) !important;

  &.full {
    width: 100%;
    margin: 0 0 2rem 0;
  }

  &:last-child {
    margin: 0 0 2rem 0;
  }

  .v-card__title {
    background-color: #e2f1ff;
  }
}

.rate-table {
  border-spacing: 0;

  th,
  td {
    color: #000;
    padding: 0.5rem 0.75rem;
  }
  tr:first-child th,
  tr:first-child td {
    border-bottom: 1px solid #ccc;
  }
  tr th:first-child {
    padding-left: 0;
  }
}

.partner-disabled {
  color: grey !important;
}

.partner-enabled {
  color: #242424 !important;
}

#statusDescription {
  margin: 0.5rem 0 2rem 0;
  padding-left: 8rem;
}

.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg {
  background-color: rgba(0, 0, 0) !important;
}
</style>
